import { useMemo, useState } from 'react';
import {
  AlertTriangle,
  ArrowLeft,
  CheckSquare,
  ChevronLeft,
  Coins,
  ExternalLink,
  Flame,
  FolderKanban,
  ListChecks,
  Plus,
  ScrollText,
  Sparkles,
  TrendingUp,
} from 'lucide-react';
import { Link } from 'react-router-dom';

import { useSession } from '../../app/useSession';
import { useTeamMember } from '../../app/useTeamMember';
import { cn } from '../../lib/cn';
import { formatHebrewRelative, hebrewDaysWord } from '../../lib/relativeTime';
import { LeadDrawer } from '../crm/leads/LeadDrawer';
import { useLeadSources } from '../crm/leads/leadsQueries';
import { TaskDrawer } from '../delivery/tasks/TaskDrawer';
import { useProjects } from '../delivery/projects/projectsQueries';
import { Sparkline } from './Sparkline';
import {
  useCommandCenterDecisions,
  useCommandCenterKpis,
  useHotLeads,
  useProjectsAtRisk,
  useRecentActivity,
  type ActivityRow,
  type HotLeadRow,
  type OverdueTaskRow,
  type ProjectAtRiskRow,
  type StuckProjectRow,
} from './homeQueries';
import styles from './CommandCenter.module.css';

const ICOUNT_URL = 'https://app.icount.co.il';

const AUDIT_ACTION_LABELS: Record<string, string> = {
  'leads.insert': 'נוצר ליד',
  'leads.update': 'עודכן ליד',
  'leads.delete': 'נמחק ליד',
  'leads.created': 'נוצר ליד',
  'deals.insert': 'נוצר דיל',
  'deals.update': 'עודכן דיל',
  'deals.delete': 'נמחק דיל',
  'deals.created': 'נוצר דיל',
  'companies.insert': 'נוצרה חברה',
  'companies.update': 'עודכנה חברה',
  'companies.delete': 'נמחקה חברה',
  'contacts.insert': 'נוצר איש קשר',
  'contacts.update': 'עודכן איש קשר',
  'projects.insert': 'נפתח פרויקט',
  'projects.update': 'עודכן פרויקט',
  'projects.delete': 'נסגר פרויקט',
  'tasks.insert': 'נוצרה משימה',
  'tasks.update': 'עודכנה משימה',
  'tasks.delete': 'נמחקה משימה',
  'deal_invoices.insert': 'נוצרה חשבונית',
  'deal_invoices.update': 'עודכנה חשבונית',
  'deal_invoices.delete': 'נמחקה חשבונית',
  'org.bootstrapped': 'הוקם ארגון',
};

function labelFor(action: string): string {
  return AUDIT_ACTION_LABELS[action] ?? action;
}

function moneyHe(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat('he-IL', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${String(amount)} ${currency}`;
  }
}

function timeOfDayGreeting(d: Date): string {
  const h = d.getHours();
  if (h < 5) return 'לילה טוב';
  if (h < 12) return 'בוקר טוב';
  if (h < 17) return 'צהריים טובים';
  if (h < 21) return 'ערב טוב';
  return 'לילה טוב';
}

const HEBREW_DAYS = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];
const HEBREW_MONTHS = [
  'ינואר',
  'פברואר',
  'מרץ',
  'אפריל',
  'מאי',
  'יוני',
  'יולי',
  'אוגוסט',
  'ספטמבר',
  'אוקטובר',
  'נובמבר',
  'דצמבר',
];

function fullHebrewDate(d: Date): string {
  const day = HEBREW_DAYS[d.getDay()];
  const dd = String(d.getDate()).padStart(2, '0');
  const month = HEBREW_MONTHS[d.getMonth()];
  const yyyy = String(d.getFullYear());
  return `יום ${day ?? ''} · ${dd} ${month ?? ''} ${yyyy}`;
}

function clockHe(d: Date): string {
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function StuckProjectItem({ project }: { project: StuckProjectRow }) {
  return (
    <li className={styles.decisionRow}>
      <Link to="/projects" className={styles.decisionLink}>
        <span className={styles.decisionBadge} data-tone="stuck">
          תקוע
        </span>
        <span className={styles.decisionTitle}>{project.name}</span>
        <span className={styles.decisionMeta}>{project.customer_name ?? '—'}</span>
        <span className={styles.decisionRight}>
          <span className={styles.decisionDays}>{hebrewDaysWord(project.days_in_status)}</span>
          {project.value_amount > 0 ? (
            <span className={styles.decisionValue} dir="ltr">
              {moneyHe(project.value_amount, project.value_currency)}
            </span>
          ) : null}
          <ChevronLeft size={14} aria-hidden="true" />
        </span>
      </Link>
    </li>
  );
}

function HotLeadAwaitingItem({ lead }: { lead: HotLeadRow }) {
  return (
    <li className={styles.decisionRow}>
      <Link to="/leads" className={styles.decisionLink}>
        <span className={styles.decisionBadge} data-tone="hot">
          ליד חם
        </span>
        <span className={styles.decisionTitle}>{lead.full_name}</span>
        <span className={styles.decisionMeta}>{lead.company_name ?? '—'}</span>
        <span className={styles.decisionRight}>
          <span className={styles.decisionDays}>{formatHebrewRelative(lead.received_at)}</span>
          {lead.score !== null ? (
            <span className={styles.decisionScore}>
              <span className="num">{String(lead.score)}</span>
            </span>
          ) : null}
          <ChevronLeft size={14} aria-hidden="true" />
        </span>
      </Link>
    </li>
  );
}

function OverdueTaskItem({ task }: { task: OverdueTaskRow }) {
  return (
    <li className={styles.decisionRow}>
      <Link to="/tasks" className={styles.decisionLink}>
        <span className={styles.decisionBadge} data-tone="overdue">
          באיחור
        </span>
        <span className={styles.decisionTitle}>{task.title}</span>
        <span className={styles.decisionMeta}>{task.project_name ?? 'ללא פרויקט'}</span>
        <span className={styles.decisionRight}>
          <span className={styles.decisionDays}>{formatHebrewRelative(task.due_date)}</span>
          <ChevronLeft size={14} aria-hidden="true" />
        </span>
      </Link>
    </li>
  );
}

function HotLeadCardRow({ lead }: { lead: HotLeadRow }) {
  return (
    <li className={styles.listRow}>
      <Link to="/leads" className={styles.listLink}>
        <span className={styles.listTitle}>{lead.full_name}</span>
        <span className={styles.listMeta}>{lead.company_name ?? '—'}</span>
        <span className={styles.listRight}>
          {lead.score !== null ? (
            <span className={styles.scorePill}>
              <span className="num">{String(lead.score)}</span>
            </span>
          ) : null}
          <span className={styles.listTime}>{formatHebrewRelative(lead.received_at)}</span>
        </span>
      </Link>
    </li>
  );
}

function ProjectAtRiskRowItem({ project }: { project: ProjectAtRiskRow }) {
  return (
    <li className={styles.listRow}>
      <Link to="/projects" className={styles.listLink}>
        <span className={styles.listTitle}>{project.name}</span>
        <span className={styles.listMeta}>
          {project.reason === 'paused' ? 'בהקפאה' : 'תקוע'}
        </span>
        <span className={styles.listRight}>
          <AlertTriangle size={14} aria-hidden="true" />
        </span>
      </Link>
    </li>
  );
}

function ActivityItem({ row }: { row: ActivityRow }) {
  return (
    <li className={styles.activityRow}>
      <span className={styles.activityLabel}>{labelFor(row.action)}</span>
      <span className={styles.activityTime}>{formatHebrewRelative(row.created_at)}</span>
    </li>
  );
}

export function CommandCenter() {
  const session = useSession();
  const sessionObj = session.status === 'signed-in' ? session.session : null;
  const userId = sessionObj?.user.id ?? null;
  const userEmail = sessionObj?.user.email ?? '';
  const userName = userEmail ? (userEmail.split('@')[0] ?? userEmail) : '';
  const { data: teamMember } = useTeamMember(sessionObj);
  const orgId = teamMember?.organization_id ?? null;

  const { data: kpis, isLoading: kpisLoading } = useCommandCenterKpis(orgId);
  const { data: decisions, isLoading: decisionsLoading } = useCommandCenterDecisions(orgId);
  const { data: hotLeads = [] } = useHotLeads(orgId);
  const { data: projectsAtRisk = [] } = useProjectsAtRisk(orgId);
  const { data: activity = [], isLoading: activityLoading } = useRecentActivity(orgId);

  // Drawer dependencies
  const { data: leadSources = [] } = useLeadSources(orgId);
  const { data: projects = [] } = useProjects(orgId);

  const [leadDrawerOpen, setLeadDrawerOpen] = useState(false);
  const [taskDrawerOpen, setTaskDrawerOpen] = useState(false);

  const now = useMemo(() => new Date(), []);
  const greeting = timeOfDayGreeting(now);
  const dateLine = fullHebrewDate(now);
  const timeLine = clockHe(now);

  const stuckProjects = decisions?.stuckProjects ?? [];
  const hotAwaiting = decisions?.hotLeadsAwaiting ?? [];
  const overdue = decisions?.overdueTasks ?? [];
  const decisionsTotal = stuckProjects.length + hotAwaiting.length + overdue.length;

  const headline = decisionsLoading
    ? null
    : decisionsTotal === 0
      ? 'אין החלטות פתוחות כרגע. נצל את הזמן.'
      : `היום צריך ${String(decisionsTotal)} ${decisionsTotal === 1 ? 'החלטה' : 'החלטות'}.`;

  const monthIncome = kpis?.monthIncome ?? 0;
  const monthCurrency = kpis?.monthIncomeCurrency ?? 'ILS';
  const pipelineValue = kpis?.pipelineOpenValue ?? 0;
  const pipelineCount = kpis?.pipelineOpenCount ?? 0;
  const weeklyLeads = kpis?.weeklyLeads ?? 0;
  const weeklyHot = kpis?.weeklyHotLeads ?? 0;
  const todayTasks = kpis?.todayTasks ?? 0;
  const overdueTaskCount = kpis?.overdueTasks ?? 0;
  const sparkline = kpis?.leads24hSparkline ?? [0, 0, 0, 0, 0, 0];
  const avgDays = kpis?.avgDaysToClose ?? null;

  const projectOptions = projects.map((p) => ({ id: p.id, name: p.name }));

  return (
    <div className={styles.page}>
      <header className={styles.hero}>
        <div className={styles.heroMain}>
          <p className={styles.heroEyebrow}>
            <span>{greeting}</span>
            <span aria-hidden="true">·</span>
            <span>{dateLine}</span>
            <span aria-hidden="true">·</span>
            <span className="num" dir="ltr">
              {timeLine}
            </span>
          </p>
          <h1 className={styles.heroTitle}>
            שלום
            {userName ? (
              <>
                , <bdi>{userName}</bdi>
              </>
            ) : null}
            {headline ? <span className={styles.heroAccent}>{headline}</span> : null}
          </h1>
          <p className={styles.heroSub}>מצב הלידים, הלקוחות, הפרויקטים והמשימות — במסך אחד.</p>
        </div>
        <nav className={styles.quickAddBar} aria-label="פעולות מהירות">
          <button
            type="button"
            className={styles.quickAdd}
            onClick={() => {
              setLeadDrawerOpen(true);
            }}
          >
            <Plus size={14} aria-hidden="true" />
            ליד
          </button>
          <button
            type="button"
            className={styles.quickAdd}
            onClick={() => {
              setTaskDrawerOpen(true);
            }}
          >
            <Plus size={14} aria-hidden="true" />
            משימה
          </button>
        </nav>
      </header>

      <section className={styles.kpiGrid} aria-label="מדדים">
        <Link to="/finance" className={cn(styles.kpiTile, styles.kpiYellow)}>
          <span className={styles.kpiHead}>
            <span className={styles.kpiLabel}>הכנסות החודש</span>
            <span className={styles.kpiIcon} aria-hidden="true">
              <Coins size={16} />
            </span>
          </span>
          <span className={styles.kpiValue} dir="ltr">
            {kpisLoading ? '…' : moneyHe(monthIncome, monthCurrency)}
          </span>
          <span className={styles.kpiNote}>חשבוניות ששולמו החודש</span>
          <span className={styles.kpiArrow} aria-hidden="true">
            <ArrowLeft size={14} />
          </span>
        </Link>

        <Link to="/projects" className={cn(styles.kpiTile, styles.kpiTeal)}>
          <span className={styles.kpiHead}>
            <span className={styles.kpiLabel}>עסקאות פתוחות</span>
            <span className={styles.kpiIcon} aria-hidden="true">
              <ScrollText size={16} />
            </span>
          </span>
          <span className={styles.kpiValue} dir="ltr">
            {kpisLoading ? '…' : moneyHe(pipelineValue, 'ILS')}
          </span>
          <span className={styles.kpiNote}>
            <span className="num">{String(pipelineCount)}</span> עסקאות באוויר
            {avgDays !== null ? (
              <>
                {' · '}
                <span className={styles.kpiNoteIcon} aria-hidden="true">
                  <TrendingUp size={12} />
                </span>{' '}
                ממוצע <span className="num">{String(avgDays)}</span> ימים לסגירה
              </>
            ) : null}
          </span>
          <span className={styles.kpiArrow} aria-hidden="true">
            <ArrowLeft size={14} />
          </span>
        </Link>

        <Link to="/leads" className={cn(styles.kpiTile, styles.kpiCoral)}>
          <span className={styles.kpiHead}>
            <span className={styles.kpiLabel}>לידים השבוע</span>
            <span className={styles.kpiIcon} aria-hidden="true">
              <Flame size={16} />
            </span>
          </span>
          <span className={styles.kpiValue}>
            <span className="num">{kpisLoading ? '…' : String(weeklyLeads)}</span>
          </span>
          <span className={styles.kpiNote}>
            <span className="num">{String(weeklyHot)}</span> חמים
          </span>
          <Sparkline
            values={sparkline}
            ariaLabel={`גרף לידים 24 שעות, ${String(weeklyLeads)} השבוע`}
          />
          <span className={styles.kpiArrow} aria-hidden="true">
            <ArrowLeft size={14} />
          </span>
        </Link>

        <Link to="/tasks" className={cn(styles.kpiTile, styles.kpiOlive)}>
          <span className={styles.kpiHead}>
            <span className={styles.kpiLabel}>משימות להיום</span>
            <span className={styles.kpiIcon} aria-hidden="true">
              <CheckSquare size={16} />
            </span>
          </span>
          <span className={styles.kpiValue}>
            <span className="num">{kpisLoading ? '…' : String(todayTasks)}</span>
          </span>
          <span className={styles.kpiNote}>
            {overdueTaskCount > 0 ? (
              <span className={styles.kpiAlert}>
                <span className="num">{String(overdueTaskCount)}</span> באיחור
              </span>
            ) : (
              'אין משימות באיחור'
            )}
          </span>
          <span className={styles.kpiArrow} aria-hidden="true">
            <ArrowLeft size={14} />
          </span>
        </Link>
      </section>

      <section className={styles.decisionsCard} aria-label="צריכים החלטה">
        <header className={styles.decisionsHead}>
          <div>
            <h2>
              <Sparkles size={16} aria-hidden="true" /> צריכים החלטה
            </h2>
            <p className={styles.decisionsSub}>הכל שדורש פעולה ממך — במקום אחד.</p>
          </div>
          <span className={styles.decisionsCount}>
            <span className="num">{String(decisionsTotal)}</span> פתוחות
          </span>
        </header>

        {decisionsLoading ? (
          <p className={styles.notice}>טוען...</p>
        ) : decisionsTotal === 0 ? (
          <p className={styles.notice}>אין דברים פתוחים. צור ליד או דיל חדש.</p>
        ) : (
          <ul className={styles.decisionsList}>
            {stuckProjects.map((p) => (
              <StuckProjectItem key={`stuck-${p.id}`} project={p} />
            ))}
            {hotAwaiting.map((l) => (
              <HotLeadAwaitingItem key={`hot-${l.id}`} lead={l} />
            ))}
            {overdue.map((t) => (
              <OverdueTaskItem key={`overdue-${t.id}`} task={t} />
            ))}
          </ul>
        )}
      </section>

      <section className={styles.twoCol} aria-label="לידים חמים ופרויקטים בסיכון">
        <div className={styles.panel}>
          <header className={styles.panelHead}>
            <h2>
              <Flame size={14} aria-hidden="true" /> לידים חמים
            </h2>
            <Link to="/leads" className={styles.panelLink}>
              כל הלידים
            </Link>
          </header>
          {hotLeads.length === 0 ? (
            <p className={styles.notice}>אין לידים חמים פתוחים.</p>
          ) : (
            <ul className={styles.list}>
              {hotLeads.map((l) => (
                <HotLeadCardRow key={l.id} lead={l} />
              ))}
            </ul>
          )}
        </div>

        <div className={styles.panel}>
          <header className={styles.panelHead}>
            <h2>
              <FolderKanban size={14} aria-hidden="true" /> פרויקטים בסיכון
            </h2>
            <Link to="/projects" className={styles.panelLink}>
              כל הפרויקטים
            </Link>
          </header>
          {projectsAtRisk.length === 0 ? (
            <p className={styles.notice}>אין פרויקטים בסיכון. מצוין.</p>
          ) : (
            <ul className={styles.list}>
              {projectsAtRisk.map((p) => (
                <ProjectAtRiskRowItem key={p.id} project={p} />
              ))}
            </ul>
          )}
        </div>
      </section>

      <section className={styles.twoCol} aria-label="כסף ופעילות">
        <a
          href={ICOUNT_URL}
          target="_blank"
          rel="noreferrer noopener"
          className={cn(styles.panel, styles.panelLink2icount)}
          title="פתח את iCount"
        >
          <header className={styles.panelHead}>
            <h2>
              <ScrollText size={14} aria-hidden="true" /> חשבוניות · iCount
            </h2>
            <span className={styles.panelLink}>
              פתח <ExternalLink size={12} aria-hidden="true" />
            </span>
          </header>
          <p className={styles.icountBody}>
            ניהול חשבוניות נשאר ב-iCount. תפתח את הלוח, תשלח חשבונית חדשה או תסמן ששולמה.
          </p>
        </a>

        <div className={styles.panel}>
          <header className={styles.panelHead}>
            <h2>
              <ListChecks size={14} aria-hidden="true" /> פעילות אחרונה
            </h2>
            <Link to="/settings/audit" className={styles.panelLink}>
              הכל
            </Link>
          </header>
          {activityLoading ? <p className={styles.notice}>טוען...</p> : null}
          {!activityLoading && activity.length === 0 ? (
            <p className={styles.notice}>אין עדיין פעילות. צור ליד, חברה או פרויקט כדי להתחיל.</p>
          ) : null}
          {!activityLoading && activity.length > 0 ? (
            <ul className={styles.activityList}>
              {activity.map((row) => (
                <ActivityItem key={row.id} row={row} />
              ))}
            </ul>
          ) : null}
        </div>
      </section>

      <LeadDrawer
        open={leadDrawerOpen}
        lead={null}
        sources={leadSources}
        orgId={orgId}
        userId={userId}
        onClose={() => {
          setLeadDrawerOpen(false);
        }}
      />

      <TaskDrawer
        open={taskDrawerOpen}
        task={null}
        projects={projectOptions}
        orgId={orgId}
        userId={userId}
        onClose={() => {
          setTaskDrawerOpen(false);
        }}
      />

    </div>
  );
}
